'use client';

import { motion } from 'framer-motion';
import { useLocale } from 'next-intl';

const EMAIL = 'hello@promptive.agency';
const PHONE_DISPLAY = '+370 612 08 887';
const PHONE_TEL = '+37061208887';
const WHATSAPP_URL = 'https://wa.me/37061208887';

const translations = {
  en: {
    eyebrow: 'Write to us',
    title: 'Send us an email',
    subtitle: 'Tell us what you want to automate. We read every message and reply personally.',
    emailCta: 'Email us',
    orDirect: 'Or reach us here:',
    whatsapp: 'WhatsApp',
    call: 'Call',
    responseTime: 'We reply within 24 hours',
  },
  lt: {
    eyebrow: 'Parašykite mums',
    title: 'Siųskite mums el. laišką',
    subtitle: 'Parašykite, ką norėtumėte automatizuoti. Perskaitome kiekvieną žinutę ir atsakome asmeniškai.',
    emailCta: 'Rašyti el. laišką',
    orDirect: 'Arba susisiekite čia:',
    whatsapp: 'WhatsApp',
    call: 'Skambinti',
    responseTime: 'Atsakome per 24 valandas',
  },
};

const MailIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const WhatsAppIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.746" />
  </svg>
);

const PhoneIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

/**
 * Direct-email call to action. Replaces the Calendly booking widget that
 * previously filled this slot on the homepage and the contact page.
 *
 * `variant` matches the surrounding background: 'dark' sits on the homepage's
 * purple gradient, 'light' on the contact page's white section.
 */
export default function EmailCtaPanel({ variant = 'light' }: { variant?: 'dark' | 'light' }) {
  const locale = useLocale();
  const t = translations[locale as keyof typeof translations] || translations.en;
  const isDark = variant === 'dark';

  const shell = isDark
    ? 'bg-white/[0.07] border-white/15 backdrop-blur-xl'
    : 'bg-white border-gray-100 shadow-xl';

  const titleColor = isDark ? 'text-white' : 'text-gray-900';
  const subtitleColor = isDark ? 'text-gray-300' : 'text-gray-600';
  const eyebrowColor = isDark ? 'text-purple-300' : 'text-purple-600';
  const dividerColor = isDark ? 'border-white/10' : 'border-gray-100';
  const mutedColor = isDark ? 'text-gray-400' : 'text-gray-500';

  const secondaryLink = isDark
    ? 'bg-white/5 border-white/10 text-gray-200 hover:bg-white/10 hover:border-white/20'
    : 'bg-gray-50 border-gray-100 text-gray-700 hover:bg-gray-100 hover:border-gray-200';

  return (
    <div className={`rounded-2xl border p-8 sm:p-10 ${shell}`}>
      {/* Icon badge */}
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg shadow-purple-500/25 mb-6">
        <MailIcon className="w-7 h-7 text-white" />
      </div>

      <span className={`text-xs font-semibold uppercase tracking-widest ${eyebrowColor}`}>
        {t.eyebrow}
      </span>
      <h3 className={`text-2xl sm:text-3xl font-bold mt-2 mb-3 ${titleColor}`}>
        {t.title}
      </h3>
      <p className={`text-base leading-relaxed mb-8 ${subtitleColor}`}>
        {t.subtitle}
      </p>

      {/* Primary CTA — the email address itself is the hero element */}
      <motion.a
        href={`mailto:${EMAIL}`}
        whileHover={{ y: -2 }}
        whileTap={{ y: 0 }}
        className="group flex flex-col items-center justify-center w-full rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-5 text-center shadow-lg shadow-purple-500/25 transition-shadow hover:shadow-xl hover:shadow-purple-500/40"
      >
        <span className="text-white/80 text-xs font-medium uppercase tracking-wider mb-1">
          {t.emailCta}
        </span>
        <span className="text-white text-lg sm:text-xl font-bold break-all">
          {EMAIL}
        </span>
      </motion.a>

      {/* Response-time signal */}
      <div className="mt-5 flex items-center justify-center gap-2.5">
        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
        <span className={`text-sm font-medium ${isDark ? 'text-green-300' : 'text-green-600'}`}>
          {t.responseTime}
        </span>
      </div>

      {/* Secondary channels */}
      <div className={`mt-8 pt-6 border-t ${dividerColor}`}>
        <p className={`text-sm mb-4 ${mutedColor}`}>{t.orDirect}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${secondaryLink}`}
          >
            <WhatsAppIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span>{t.whatsapp}</span>
          </a>
          <a
            href={`tel:${PHONE_TEL}`}
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${secondaryLink}`}
          >
            <PhoneIcon className="w-5 h-5 text-purple-400 flex-shrink-0" />
            <span className="whitespace-nowrap">{PHONE_DISPLAY}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
